import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useWallet } from '@txnlab/use-wallet-react'
import algosdk from 'algosdk'
import { useSnackbar } from 'notistack'
import { getAlgodConfigFromViteEnvironment, getIndexerConfigFromViteEnvironment } from '../utils/network/getAlgoClientConfigs'
import { FAUCET_APP_ID } from '../constants/faucet'
import { SPIN_APP_ID, SPIN_COST } from '../constants/spin'

function PixelLoader() {
  return (
    <div className="px-loader px-body-center" aria-hidden="true">
      <span />
      <span />
      <span />
    </div>
  )
}

const CONTRACT_ID = FAUCET_APP_ID
const CREATOR_ADDRESS = 'GET2TVGHL7H3UFFIY4VGIKYYXIVBDX6VCRZBEEW7GEHLHEGPYVLYKJRAVM'
const DELETE_LSIG_ADDRESS = 'UROWNIR6GS4KFOEG2T4DY6BVQG5YNDZJFUBMHNVP2QJPB54A4ZJIRYY3DI'
const CONFIG_FEE = 8_000

type SpinBox = {
  addr: string
  locked: boolean
  pending: number
  spins: number
  pnl: number
  lastRound: number
}

function formatPnl(micro: number) {
  const sign = micro > 0 ? '+' : micro < 0 ? '-' : ''
  return `${sign}${(Math.abs(micro) / 1_000_000).toFixed(3)}`
}

type BoxInfo = {
  addr: string
  locked: boolean
  lastClaimRound: number
  lastClaimTimestamp?: number | null
  totalClaims: number
}

const CreatorTools: React.FC = () => {
  const { activeAddress, transactionSigner } = useWallet()
  const { enqueueSnackbar } = useSnackbar()
  const [open, setOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'boxes' | 'withdraw' | 'spin'>('boxes')
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [boxes, setBoxes] = useState<BoxInfo[]>([])
  const [loadingBoxes, setLoadingBoxes] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [userSearch, setUserSearch] = useState('')
  const [totalBalance, setTotalBalance] = useState<number>(0)
  const [minBalance, setMinBalance] = useState<number>(0)
  const [reserved, setReserved] = useState<number>(0)
  const available = useMemo(() => Math.max(0, totalBalance - minBalance - reserved), [totalBalance, minBalance, reserved])
  const [withdrawAmt, setWithdrawAmt] = useState<string>('')
  const [loadingWithdraw, setLoadingWithdraw] = useState(false)
  const [withdrawTarget, setWithdrawTarget] = useState<'FAUCET' | 'SPIN'>('FAUCET')
  const [spinBoxes, setSpinBoxes] = useState<SpinBox[]>([])
  const [loadingSpin, setLoadingSpin] = useState(false)
  const [spinSearch, setSpinSearch] = useState('')
  const [spinRound, setSpinRound] = useState(0)


  const algodConfig = getAlgodConfigFromViteEnvironment()
  const algodClient = new algosdk.Algodv2(String(algodConfig.token), algodConfig.server, algodConfig.port)

  const indexerConfig = useMemo(() => {
    try {
      return getIndexerConfigFromViteEnvironment()
    } catch {
      return null
    }
  }, [])

  const indexerClient = useMemo(() => {
    if (!indexerConfig) return null
    try {
      return new algosdk.Indexer(String(indexerConfig.token), indexerConfig.server, indexerConfig.port)
    } catch {
      return null
    }
  }, [indexerConfig])

  const shortenAddr = (a: string) => (a && a.length > 12 ? `${a.slice(0, 4)}...${a.slice(-4)}` : a)

  const decodeBoxState = (boxValue: any): { locked: boolean; lastClaimRound: number } => {
    try {
      const bytes = new Uint8Array(boxValue)
      if (bytes.length < 8) return { locked: false, lastClaimRound: 0 }
      const buf = Buffer.from(bytes)
      if (bytes.length >= 9) {
        return {
          locked: bytes[0] === 1,
          lastClaimRound: Number(buf.readBigUInt64BE(1)),
        }
      }
      return {
        locked: false,
        lastClaimRound: Number(buf.readBigUInt64BE(0)),
      }
    } catch {
      return { locked: false, lastClaimRound: 0 }
    }
  }

  const normalizedSpinSearch = spinSearch.trim().toLowerCase()
  const filteredSpinBoxes = useMemo(
    () => (
      normalizedSpinSearch
        ? spinBoxes.filter((box) => box.addr.toLowerCase().includes(normalizedSpinSearch))
        : spinBoxes
    ),
    [spinBoxes, normalizedSpinSearch]
  )

  const normalizedSearch = userSearch.trim().toLowerCase()
  const filteredBoxes = useMemo(
    () => (
      normalizedSearch
        ? boxes.filter((box) => box.addr.toLowerCase().includes(normalizedSearch))
        : boxes
    ),
    [boxes, normalizedSearch]
  )

  const formatLastClaimTime = (timestampSec?: number | null): string => {
    if (!timestampSec) return 'Unknown'

    const date = new Date(timestampSec * 1000)
    const now = Date.now()
    const diffMs = now - date.getTime()
    const diffMinutes = Math.floor(diffMs / 60000)

    let relative: string
    if (diffMinutes < 1) {
      relative = 'just now'
    } else if (diffMinutes < 60) {
      relative = `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`
    } else {
      const diffHours = Math.floor(diffMinutes / 60)
      if (diffHours < 24) {
        relative = `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
      } else {
        const diffDays = Math.floor(diffHours / 24)
        relative = `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
      }
    }

    return relative
  }

  const pLimit = (n: number) => {
    let active = 0
    const queue: Array<() => void> = []
    const next = () => {
      active--
      const fn = queue.shift()
      if (fn) fn()
    }
    return async <T,>(fn: () => Promise<T>): Promise<T> =>
      await new Promise<T>((resolve, reject) => {
        const run = () => {
          active++
          fn().then(resolve, reject).finally(next)
        }
        if (active < n) run()
        else queue.push(run)
      })
  }

  const fetchClaimStats = async (_contractAddr: string, userAddr: string): Promise<{ totalClaims: number; lastClaimTimestamp?: number | null }> => {
    if (!indexerClient) {
      console.log('No indexer client available for', userAddr)
      // Fallback: utiliser une estimation basée sur le fait qu'il y a une box
      // Si une box existe, il y a au moins eu un claim
      return { totalClaims: 1 }
    }
    try {
      // Rechercher les transactions d'application "claim" pour cet utilisateur
      const appRes = await (indexerClient as any)
        .searchForTransactions()
        .applicationID(CONTRACT_ID)
        .txType('appl')
        .address(userAddr)
        .addressRole('sender')
        .limit(1000)
        .do()

      const appTxs = (appRes?.transactions || []) as any[]
      const claimTxs = appTxs.filter((tx: any) => {
        const appArgs = tx?.applicationTransaction?.applicationArgs || []
        if (appArgs.length === 0) return false
        try {
          const firstArg = Buffer.from(appArgs[0], 'base64').toString()
          return firstArg === 'claim'
        } catch {
          return false
        }
      })

      let totalClaims = claimTxs.length
      console.log(`Found ${totalClaims} claim transactions for ${userAddr}`)

      // Déterminer le timestamp du dernier claim à partir des transactions trouvées
      let lastClaimTimestamp: number | null = null
      if (claimTxs.length > 0) {
        try {
          const latestTx = claimTxs.reduce((acc, tx) => {
            const accRt = (acc as any)['round-time'] ?? (acc as any).roundTime ?? 0
            const txRt = (tx as any)['round-time'] ?? (tx as any).roundTime ?? 0
            return txRt > accRt ? tx : acc
          }, claimTxs[0])
          const rt = (latestTx as any)['round-time'] ?? (latestTx as any).roundTime
          if (typeof rt === 'number') {
            lastClaimTimestamp = rt
          }
        } catch {
          lastClaimTimestamp = null
        }
      }

      // Si aucune transaction trouvée mais qu'une box existe, il y a eu au moins un claim
      // (une box est créée lors du premier claim)
      if (totalClaims === 0) {
        totalClaims = 1 // Au moins un claim si une box existe
      }

      console.log(`Stats for ${userAddr}: ${totalClaims} claims, lastClaimTimestamp=${lastClaimTimestamp}`)
      return { totalClaims, lastClaimTimestamp }
    } catch (e) {
      console.error('Failed to fetch claim stats:', e, userAddr)
      return { totalClaims: 0 }
    }
  }

  const refreshBoxes = async () => {
    const known = new Map(boxes.map((box) => [box.addr, box]))
    if (known.size === 0) setLoadingBoxes(true)
    try {
      const contractAddr = algosdk.getApplicationAddress(CONTRACT_ID)
      const res = await algodClient.getApplicationBoxes(CONTRACT_ID).do()
      const names: Uint8Array[] = []
      for (const b of res.boxes || []) {
        const nameB64 = (b as any)?.name
        if (!nameB64) continue
        try {
          const bytes = Uint8Array.from(Buffer.from(nameB64, 'base64'))
          names.push(bytes)
        } catch {}
      }
      const sortBoxes = (rows: BoxInfo[]) => rows.sort((a, b) => (b.lastClaimRound - a.lastClaimRound) || a.addr.localeCompare(b.addr))
      const limit = pLimit(10)
      const infos = await Promise.all(
        names.map((name) =>
          limit(async () => {
            const addr = algosdk.encodeAddress(name)
            const previous = known.get(addr)
            let decoded = { locked: previous?.locked ?? false, lastClaimRound: previous?.lastClaimRound ?? 0 }
            try {
              const box = await algodClient.getApplicationBoxByName(CONTRACT_ID, name).do()
              if (box?.value) decoded = decodeBoxState(box.value)
            } catch {}
            const stats = await fetchClaimStats(contractAddr, addr)
            const lastClaimTimestamp = typeof stats.lastClaimTimestamp === 'number' ? stats.lastClaimTimestamp : previous?.lastClaimTimestamp ?? null
            return {
              addr,
              locked: decoded.locked,
              lastClaimRound: decoded.lastClaimRound,
              lastClaimTimestamp,
              totalClaims: stats.totalClaims || previous?.totalClaims || 0,
            }
          }),
        ),
      )
      setBoxes(sortBoxes(infos))
    } catch (e) {
      enqueueSnackbar('Failed to load boxes', { variant: 'error' })
    }
    setLoadingBoxes(false)
  }

  const refreshContractStats = async (target: 'FAUCET' | 'SPIN' = withdrawTarget) => {
    setLoadingWithdraw(true)
    try {
      const appId = target === 'SPIN' ? SPIN_APP_ID : CONTRACT_ID
      const appAddr = algosdk.getApplicationAddress(appId)
      const info = await algodClient.accountInformation(appAddr).do()
      const total = Number(info.amount) / 1_000_000
      const min = Number((info as any).minBalance ?? (info as any)['min-balance'] ?? 0) / 1_000_000
      let reserved = 0
      if (target === 'SPIN') {
        const app = await algodClient.getApplicationByID(SPIN_APP_ID).do()
        const globalState = (app as any).params?.globalState || (app as any).params?.['global-state'] || []
        for (const entry of globalState) {
          const raw = entry.key
          const key = typeof raw === 'string'
            ? Buffer.from(raw, 'base64').toString('utf8').replace(/\0/g, '')
            : new TextDecoder().decode(raw).replace(/\0/g, '')
          if (key !== 'open') continue
          const uintRaw = entry.value?.uint ?? entry.value?.value?.uint
          const open = Number(uintRaw ?? 0)
          if (!Number.isNaN(open)) reserved = open
        }
      }
      setTotalBalance(total)
      setMinBalance(min)
      setReserved(reserved)
      setWithdrawAmt(String(Math.max(0, total - min - reserved)))
    } catch (e) {
      enqueueSnackbar('Failed to fetch contract stats', { variant: 'error' })
    }
    setLoadingWithdraw(false)
  }

  useEffect(() => {
    if (!open) return
    if (activeTab === 'boxes') refreshBoxes()
    if (activeTab === 'withdraw') refreshContractStats()
    if (activeTab === 'spin') refreshSpinBoxes()
  }, [open, activeTab])

  const switchTab = (tab: 'boxes' | 'withdraw' | 'spin') => {
    if (tab === activeTab) return
    setActiveTab(tab)
    if (contentRef.current) contentRef.current.scrollTop = 0
  }

  const performWithdraw = async (amtAlgo: number) => {
    if (!activeAddress || !transactionSigner) {
      enqueueSnackbar('Please connect your wallet first', { variant: 'error' })
      return
    }
    if (!isFinite(amtAlgo) || amtAlgo <= 0) {
      enqueueSnackbar('Invalid amount', { variant: 'error' })
      return
    }
    const amt = Math.round(amtAlgo * 1_000_000)
    const amtArg = new Uint8Array(8)
    new DataView(amtArg.buffer).setBigUint64(0, BigInt(amt))
    try {
      const sp = await algodClient.getTransactionParams().do()
      const txn = algosdk.makeApplicationCallTxnFromObject({
        sender: activeAddress,
        appIndex: withdrawTarget === 'SPIN' ? SPIN_APP_ID : CONTRACT_ID,
        onComplete: algosdk.OnApplicationComplete.NoOpOC,
        appArgs: [new TextEncoder().encode('withdraw'), amtArg],
        suggestedParams: { ...sp, flatFee: true, fee: 2000 },
      })
      const signed = await transactionSigner([txn], [0])
      const res = await algodClient.sendRawTransaction(signed).do()
      await algosdk.waitForConfirmation(algodClient, res.txid, 10)
      enqueueSnackbar('Withdraw successful', { variant: 'success' })
    } catch (e: any) {
      enqueueSnackbar(`Withdraw failed: ${e?.message || 'error'}`, { variant: 'error' })
    }
  }

  const performDeleteBox = async (boxAddress: string) => {
    if (!activeAddress || !transactionSigner) {
      enqueueSnackbar('Please connect your wallet first', { variant: 'error' })
      return
    }
    if (!boxAddress) return
    try {
      const suggestedParams = await algodClient.getTransactionParams().do()
      const boxName = algosdk.decodeAddress(boxAddress).publicKey
      const paymentTxn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
        sender: activeAddress,
        suggestedParams,
        amount: CONFIG_FEE,
        receiver: DELETE_LSIG_ADDRESS,
      })
      const deleteTxn = algosdk.makeApplicationCallTxnFromObject({
        sender: activeAddress,
        appIndex: CONTRACT_ID,
        appArgs: [new TextEncoder().encode('delete'), boxName],
        boxes: [{ appIndex: CONTRACT_ID, name: boxName }],
        onComplete: algosdk.OnApplicationComplete.NoOpOC,
        suggestedParams: { ...suggestedParams, flatFee: true, fee: 0 },
      })
      const [payWithGroup, deleteWithGroup] = algosdk.assignGroupID([paymentTxn, deleteTxn])
      const signed = await transactionSigner([payWithGroup as any, deleteWithGroup as any], [0, 1])
      const res = await algodClient.sendRawTransaction(signed).do()
      await algosdk.waitForConfirmation(algodClient, res.txid, 4)
      setBoxes(prev => prev.filter(b => b.addr !== boxAddress))
      enqueueSnackbar('Box deleted successfully', { variant: 'success' })
    } catch (e: any) {
      const body = e?.response?.body ?? e?.body
      const msg = typeof body === 'string' ? body : body?.message ?? e?.message ?? 'error'
      enqueueSnackbar(`Delete failed: ${msg}`, { variant: 'error' })
      console.error('Delete box error', e?.response ?? e)
    }
  }

  const performToggleLock = async (boxAddress: string, locked: boolean) => {
    if (!activeAddress || !transactionSigner) {
      enqueueSnackbar('Please connect your wallet first', { variant: 'error' })
      return
    }
    if (!boxAddress) return
    setActionLoading(true)
    try {
      const suggestedParams = await algodClient.getTransactionParams().do()
      const boxName = algosdk.decodeAddress(boxAddress).publicKey
      const appTxn = algosdk.makeApplicationCallTxnFromObject({
        sender: activeAddress,
        appIndex: CONTRACT_ID,
        appArgs: [new TextEncoder().encode(locked ? 'unlocked' : 'locked'), boxName],
        boxes: [{ appIndex: CONTRACT_ID, name: boxName }],
        onComplete: algosdk.OnApplicationComplete.NoOpOC,
        suggestedParams: { ...suggestedParams, flatFee: true, fee: 1000 },
      })
      const signed = await transactionSigner([appTxn as any], [0])
      const res = await algodClient.sendRawTransaction(signed).do()
      await algosdk.waitForConfirmation(algodClient, res.txid, 4)
      setBoxes((prev) => prev.map((b) => (b.addr === boxAddress ? { ...b, locked: !locked } : b)))
      enqueueSnackbar(locked ? 'Address unlocked' : 'Address locked', { variant: 'success' })
    } catch (e: any) {
      const actionLabel = locked ? 'Unlock' : 'Lock'
      enqueueSnackbar(`${actionLabel} failed: ${e?.message || 'error'}`, { variant: 'error' })
    }
    setActionLoading(false)
  }

  const readUint64 = (bytes: Uint8Array, offset: number) => {
    let value = 0n
    for (let i = 0; i < 8; i += 1) value = (value << 8n) + BigInt(bytes[offset + i] || 0)
    return Number(value)
  }

  const refreshSpinBoxes = async () => {
    setLoadingSpin(true)
    try {
      const status = await algodClient.status().do()
      setSpinRound(Number((status as any).lastRound ?? (status as any)['last-round'] ?? 0))
      const res = await algodClient.getApplicationBoxes(SPIN_APP_ID).do()
      const names: Uint8Array[] = []
      for (const box of res.boxes || []) {
        const nameB64 = (box as any)?.name
        if (!nameB64) continue
        const name = Uint8Array.from(Buffer.from(nameB64, 'base64'))
        if (name.length === 32) names.push(name)
      }
      const limit = pLimit(8)
      const rows = await Promise.all(names.map((name) => limit(async (): Promise<SpinBox> => {
        const addr = algosdk.encodeAddress(name)
        const stats = await fetchSpinStats(addr)
        try {
          const stored = await algodClient.getApplicationBoxByName(SPIN_APP_ID, name).do()
          const value = new Uint8Array((stored as any).value)
          return { addr, locked: value[0] === 1, pending: readUint64(value, 9), ...stats }
        } catch {
          return { addr, locked: false, pending: 0, ...stats }
        }
      })))
      rows.sort((a, b) => (b.lastRound - a.lastRound) || (b.pending - a.pending) || a.addr.localeCompare(b.addr))
      setSpinBoxes(rows)
    } catch {
      enqueueSnackbar('Failed to load spin boxes', { variant: 'error' })
      setSpinBoxes([])
    }
    setLoadingSpin(false)
  }

  const argBytes = (arg: any): Uint8Array | null => {
    try {
      if (arg instanceof Uint8Array) return arg
      if (typeof arg === 'string') return Uint8Array.from(Buffer.from(arg, 'base64'))
      return null
    } catch {
      return null
    }
  }

  const fetchSpinStats = async (userAddr: string): Promise<{ spins: number; pnl: number; lastRound: number }> => {
    if (!indexerClient) return { spins: 0, pnl: 0, lastRound: 0 }
    try {
      const appRes = await (indexerClient as any)
        .searchForTransactions()
        .applicationID(SPIN_APP_ID)
        .txType('appl')
        .address(userAddr)
        .limit(1000)
        .do()
      const txs = (appRes?.transactions || []) as any[]
      let spins = 0
      let won = 0
      let lastRound = 0
      for (const tx of txs) {
        const appArgs = tx?.applicationTransaction?.applicationArgs || tx?.['application-transaction']?.['application-args'] || []
        const first = argBytes(appArgs[0])
        if (first && new TextDecoder().decode(first) === 'spin') {
          spins += 1
          const round = Number(tx['confirmed-round'] ?? tx.confirmedRound ?? 0)
          if (round > lastRound) lastRound = round
        }
        for (const log of tx.logs || []) {
          const bytes = argBytes(log)
          if (!bytes || bytes.length < 51) continue
          if (new TextDecoder().decode(bytes.subarray(0, 3)) !== 'win') continue
          if (algosdk.encodeAddress(bytes.subarray(3, 35)) !== userAddr) continue
          won += readUint64(bytes, 43)
        }
      }
      return { spins, pnl: won - spins * SPIN_COST, lastRound }
    } catch {
      return { spins: 0, pnl: 0, lastRound: 0 }
    }
  }

  const spinCall = async (args: Uint8Array[], names: Uint8Array[], fee = 1000) => {
    if (!activeAddress || !transactionSigner) throw new Error('Please connect your wallet first')
    const suggestedParams = await algodClient.getTransactionParams().do()
    const txn = algosdk.makeApplicationNoOpTxnFromObject({
      sender: activeAddress,
      appIndex: SPIN_APP_ID,
      appArgs: args,
      boxes: names.map((name) => ({ appIndex: SPIN_APP_ID, name })),
      suggestedParams: { ...suggestedParams, flatFee: true, fee },
    })
    const signed = await transactionSigner([txn as any], [0])
    const sent = await algodClient.sendRawTransaction(signed).do()
    await algosdk.waitForConfirmation(algodClient, sent.txid, 10)
  }

  const performSpinLock = async (boxAddress: string, locked: boolean) => {
    setActionLoading(true)
    try {
      const name = algosdk.decodeAddress(boxAddress).publicKey
      await spinCall([new TextEncoder().encode(locked ? 'unlocked' : 'locked'), name], [name])
      setSpinBoxes((prev) => prev.map((box) => (box.addr === boxAddress ? { ...box, locked: !locked } : box)))
      enqueueSnackbar(locked ? 'Address unlocked' : 'Address locked', { variant: 'success' })
    } catch (e: any) {
      enqueueSnackbar(`${locked ? 'Unlock' : 'Lock'} failed: ${e?.message || 'error'}`, { variant: 'error' })
    }
    setActionLoading(false)
  }

  const performSpinClean = async (boxAddress: string) => {
    setActionLoading(true)
    try {
      const name = algosdk.decodeAddress(boxAddress).publicKey
      await spinCall([new TextEncoder().encode('clean'), name], [name])
      setSpinBoxes((prev) => prev.filter((box) => box.addr !== boxAddress))
      enqueueSnackbar('Box deleted', { variant: 'success' })
    } catch (e: any) {
      enqueueSnackbar(`Delete failed: ${e?.message || 'error'}`, { variant: 'error' })
    }
    setActionLoading(false)
  }

  const performSpinExpire = async (boxAddress: string) => {
    setActionLoading(true)
    try {
      const name = algosdk.decodeAddress(boxAddress).publicKey
      await spinCall([new TextEncoder().encode('expire'), name], [name])
      setSpinBoxes((prev) => prev.map((box) => (box.addr === boxAddress ? { ...box, pending: 0 } : box)))
      enqueueSnackbar('Expired spin cleared', { variant: 'success' })
    } catch (e: any) {
      enqueueSnackbar(`Expire failed: ${e?.message || 'error'}`, { variant: 'error' })
    }
    setActionLoading(false)
  }

  const performSpinCleanAll = async () => {
    if (!activeAddress || !transactionSigner) return
    setActionLoading(true)
    try {
      const names = spinBoxes.map((box) => algosdk.decodeAddress(box.addr).publicKey)
      for (let i = 0; i < names.length; i += 8) {
        const batch = names.slice(i, i + 8)
        await spinCall([new TextEncoder().encode('clean'), ...batch], batch)
      }
      enqueueSnackbar('All spin boxes deleted', { variant: 'success' })
      await refreshSpinBoxes()
    } catch (e: any) {
      enqueueSnackbar(`Delete all failed: ${e?.message || 'error'}`, { variant: 'error' })
    }
    setActionLoading(false)
  }

  if (!activeAddress || activeAddress !== CREATOR_ADDRESS) {
    return null
  }

  return (
    <div>
      <button
        className="px-btn px-btn-icon"
        onClick={() => {
          if (boxes.length === 0) setLoadingBoxes(true)
          setOpen(true)
        }}
        type="button"
        aria-label="Creator tools"
      >
        KEY
      </button>
      {open && (
        <div className="px-overlay" data-modal-root="true">
          <div className="px-modal px-modal-fixed">
            <div className="px-modal-head">
              <div className="px-tabs">
                <span className="px-btn">{(activeTab === 'spin' ? spinBoxes.length : boxes.length)} BOX</span>
                <button className={`px-btn${activeTab === 'boxes' ? ' is-on' : ''}`} type="button" onClick={() => switchTab('boxes')}>BOX</button>
                <button className={`px-btn${activeTab === 'spin' ? ' is-on' : ''}`} type="button" onClick={() => switchTab('spin')}>SPIN</button>
                <button className={`px-btn${activeTab === 'withdraw' ? ' is-on' : ''}`} type="button" onClick={() => switchTab('withdraw')}>OUT</button>
              </div>
              <button className="px-btn" type="button" onClick={() => setOpen(false)}>X</button>
            </div>

            {(activeTab === 'boxes' || activeTab === 'spin') && !(activeTab === 'boxes' ? loadingBoxes : loadingSpin) && (
              <div className="px-modal-search">
                <input
                  className="px-input"
                  type="text"
                  value={activeTab === 'spin' ? spinSearch : userSearch}
                  onChange={(e) => (activeTab === 'spin' ? setSpinSearch(e.target.value) : setUserSearch(e.target.value))}
                  placeholder="Search address..."
                />
              </div>
            )}

            <div ref={contentRef} className="px-body hidden-scrollbar">
              {activeTab === 'boxes' && (
                loadingBoxes ? (
                  <PixelLoader />
                ) : boxes.length === 0 ? (
                  <div>No boxes</div>
                ) : filteredBoxes.length === 0 ? (
                  <div>No matching address</div>
                ) : (
                  <>
                    {filteredBoxes.map((b) => (
                      <div key={b.addr} className={`px-row${b.locked ? ' is-locked' : ''}`}>
                        <div>
                          <a
                            className="px-mono"
                            href={`https://explorer.perawallet.app/address/${b.addr}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {shortenAddr(b.addr)}
                          </a>
                          <div>Claims {b.totalClaims} · {formatLastClaimTime(b.lastClaimTimestamp)}</div>
                        </div>
                        <div className="px-tabs">
                          <button className="px-btn px-btn-lock" type="button" disabled={actionLoading} onClick={() => performToggleLock(b.addr, b.locked)}>
                            {b.locked ? 'UNLOCK' : 'LOCK'}
                          </button>
                          <button className="px-btn" type="button" disabled={actionLoading} onClick={() => performDeleteBox(b.addr)}>DEL</button>
                        </div>
                      </div>
                    ))}
                    <div className="px-actions" style={{ marginTop: 12 }}>
                      <button
                        className="px-btn"
                        type="button"
                        onClick={async () => {
                            try {
                              const res = await algodClient.getApplicationBoxes(CONTRACT_ID).do()
                              const names: Uint8Array[] = []
                              for (const b of res.boxes || []) {
                                const nameB64 = (b as any)?.name
                                if (nameB64) {
                                  try { names.push(Uint8Array.from(Buffer.from(nameB64, 'base64'))) } catch {}
                                }
                              }
                              if (names.length === 0) return
                              const chunk = <T,>(arr: T[], size: number): T[][] => { const out: T[][] = []; for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size)); return out }
                              for (const batch of chunk(names, 8)) {
                                const sp = await algodClient.getTransactionParams().do()
                                const appArgs = [new TextEncoder().encode('clean'), ...batch]
                                const boxesArg = batch.map((n) => ({ appIndex: CONTRACT_ID, name: n }))
                                const txn = algosdk.makeApplicationCallTxnFromObject({ sender: activeAddress as string, appIndex: CONTRACT_ID, onComplete: algosdk.OnApplicationComplete.NoOpOC, appArgs, boxes: boxesArg as any, suggestedParams: sp })
                                const signed = await transactionSigner!([txn], [0])
                                const send = await algodClient.sendRawTransaction(signed).do()
                                await algosdk.waitForConfirmation(algodClient, send.txid, 15)
                              }
                              enqueueSnackbar('All boxes deleted', { variant: 'success' })
                              refreshBoxes()
                            } catch (e: any) {
                              const body = e?.response?.body ?? e?.body
                              const msg = typeof body === 'string' ? body : body?.message ?? e?.message ?? 'error'
                              enqueueSnackbar(`Delete all failed: ${msg}`, { variant: 'error' })
                              console.error('Delete many error', e?.response ?? e)
                            }
                          }}
                        >
                          DELETE ALL
                        </button>
                      </div>
                  </>
                )
              )}

            {activeTab === 'spin' && (
              loadingSpin ? (
                <PixelLoader />
              ) : spinBoxes.length === 0 ? (
                <div>No boxes</div>
              ) : filteredSpinBoxes.length === 0 ? (
                <div>No matching address</div>
              ) : (
                <>
                  {filteredSpinBoxes.map((box) => {
                    const expired = box.pending > 0 && spinRound > box.pending + 1001
                    return (
                      <div key={box.addr} className={`px-row${box.locked ? ' is-locked' : ''}`}>
                        <div>
                          <a
                            className="px-mono"
                            href={`https://explorer.perawallet.app/address/${box.addr}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {shortenAddr(box.addr)}
                          </a>
                          <div>Spins {box.spins} · PnL {formatPnl(box.pnl)}{box.pending ? ` · Seed ${box.pending}` : ''}</div>
                        </div>
                        <div className="px-tabs">
                          <button className="px-btn px-btn-lock" type="button" disabled={actionLoading} onClick={() => performSpinLock(box.addr, box.locked)}>
                            {box.locked ? 'UNLOCK' : 'LOCK'}
                          </button>
                          {expired && (
                            <button className="px-btn" type="button" disabled={actionLoading} onClick={() => performSpinExpire(box.addr)}>EXPIRE</button>
                          )}
                          <button className="px-btn" type="button" disabled={actionLoading} onClick={() => performSpinClean(box.addr)}>DEL</button>
                        </div>
                      </div>
                    )
                  })}
                  <div className="px-actions" style={{ marginTop: 12 }}>
                    <button className="px-btn" type="button" disabled={actionLoading} onClick={performSpinCleanAll}>DELETE ALL</button>
                  </div>
                </>
              )
            )}

            {activeTab === 'withdraw' && (
              loadingWithdraw ? (
                <PixelLoader />
              ) : (
              <div className="px-body-center">
                <div className="px-chip-row">
                  {(['FAUCET', 'SPIN'] as const).map((target) => (
                    <button
                      key={target}
                      type="button"
                      className={`px-btn${withdrawTarget === target ? ' is-on' : ''}`}
                      onClick={() => {
                        setWithdrawTarget(target)
                        refreshContractStats(target)
                      }}
                    >
                      {target}
                    </button>
                  ))}
                </div>
                <div className="px-stat"><span>Total</span><span>{totalBalance.toFixed(6)} ALGO</span></div>
                <div className="px-stat"><span>MBR</span><span>{minBalance.toFixed(6)} ALGO</span></div>
                <div className="px-stat"><span>Available</span><span>{available.toFixed(6)} ALGO</span></div>
                <div className="px-form px-form-row">
                  <input
                    className="px-input"
                    type="number"
                    inputMode="decimal"
                    step="1"
                    min="0"
                    value={withdrawAmt}
                    onChange={(e) => setWithdrawAmt(e.target.value)}
                    placeholder="Amount"
                  />
                  <button
                    className="px-btn"
                    type="button"
                    onClick={async () => {
                      const n = Number(withdrawAmt || available);
                      if (isFinite(n) && n > 0) {
                        await performWithdraw(n);
                        refreshContractStats()
                      } else {
                        enqueueSnackbar('Invalid amount', { variant: 'error' })
                      }
                    }}
                  >
                    SEND
                  </button>
                </div>
              </div>
              )
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CreatorTools


